"use client";

import { ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Pricing() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const grid = useRevealOnScroll<HTMLDivElement>();
  const footer = useRevealOnScroll<HTMLDivElement>();
  const packages = [{
    name: "Free 15‑Minute Call",
    tagline: "",
    price: "$0",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "Car Deal Review",
    tagline: "",
    price: "$149",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "Car Purchase or Lease Negotiation",
    tagline: "",
    price: "starting at $699",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "Full Car Buying Concierge",
    tagline: "",
    price: "starting at $999",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "Home Repair Quote Review",
    tagline: "",
    price: "$149",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "Contractor Quote Comparison",
    tagline: "",
    price: "starting at $399",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "Home Improvement Negotiation Support",
    tagline: "",
    price: "starting at $499",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }, {
    name: "In‑Person Support",
    tagline: "",
    price: "$150/hour, 2‑hour minimum",
    cadence: "",
    featured: false,
    features: [],
    cta: "Book Now"
  }];
  return <section data-section-id="252" id="fair-transparent-fees" className="bg-background text-foreground py-20 md:py-28 lg:py-32 px-6 md:px-12 lg:px-[140px]">
      <div className="max-w-7xl mx-auto">
        <div ref={header.ref} className={`text-center max-w-3xl mx-auto mb-16 md:mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs md:text-sm tracking-[0.2em] uppercase text-accent-foreground mb-6">
            Pricing / Transparent
          </p>
          <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.1] text-primary mb-6 uppercase">
            Fair & Transparent Fees
          </h2>
          <p className="font-default text-base md:text-lg text-foreground leading-relaxed">
            We believe in clear, upfront pricing. No hidden costs, just professional advocacy to help you secure the best possible deal on your next major purchase.
          </p>
        </div>

        <div ref={grid.ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-stretch transition-all duration-700 ease-out delay-100 ${grid.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {packages.map((pkg, i) => <div data-index={i} key={pkg.name} className={`relative flex flex-col rounded-lg p-8 md:p-10 ${pkg.featured ? "bg-primary text-white lg:py-14 lg:-my-6 shadow-lg z-10" : "bg-white border border-border text-foreground shadow-sm"}`}>
              {pkg.featured && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-block bg-accent text-accent-foreground font-default font-bold text-[10px] tracking-[0.2em] uppercase px-3 py-1.5 rounded-md">
                    Most Popular
                  </span>
                </div>}

              <div className="mb-6">
                <h3 className="font-default font-bold text-xl leading-tight mb-2">
                  {pkg.name}
                </h3>
                {pkg.tagline && <p className={`font-default text-[11px] tracking-[0.2em] uppercase ${pkg.featured ? "text-white/70" : "text-muted-foreground"}`}>
                    {pkg.tagline}
                  </p>}
              </div>

              <div className="flex items-baseline gap-2 mb-6">
                <span className="font-default text-3xl font-bold tracking-tight">
                  {pkg.price}
                </span>
                {pkg.cadence && <span className={`font-default text-sm ${pkg.featured ? "text-white/70" : "text-muted-foreground"}`}>
                    {pkg.cadence}
                  </span>}
              </div>

              <Separator className={pkg.featured ? "bg-white/20" : "bg-border"} />

              <ul className="flex-1 space-y-3.5 my-8">
                {pkg.features.map((feature, j) => <li data-index={j} key={feature} className="flex items-start gap-3 text-sm leading-relaxed">
                    <Check className={`size-4 mt-0.5 shrink-0 ${pkg.featured ? "text-accent" : "text-accent-foreground"}`} strokeWidth={2.5} />
                    <span className={pkg.featured ? "text-white" : "text-foreground"}>
                      {feature}
                    </span>
                  </li>)}
              </ul>

              <Button asChild size="lg" className={pkg.featured ? "bg-accent text-white hover:bg-accent/90 w-full justify-between rounded-lg" : "bg-primary text-white hover:bg-primary/90 w-full justify-between rounded-lg"}>
                <Link to="/AboutContactPage">
                  <span>{pkg.cta}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>)}
        </div>

        <div ref={footer.ref} className={`text-center mt-16 md:mt-20 transition-all duration-700 ease-out delay-200 ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-sm md:text-base text-foreground">
            Need a tailored solution? We offer custom advocacy packages for complex negotiations.{" "}
            <Link to="/AboutContactPage" className="text-accent font-bold underline underline-offset-4 hover:text-accent/80 transition-colors">
              Contact us for a custom quote
            </Link>
          </p>
        </div>
      </div>
    </section>;
}